import React from 'react'
import { Box, Typography, LinearProgress, Chip } from '@mui/material'
import { poolwerxColors } from '../../theme/colors'

interface SyncProgressBarProps {
  progress: number  // 0-100
  currentStage: string  // e.g., "🧾 Syncing sales"
  system: 'lightspeed' | 'xero' | 'skimmer'
  variant?: 'full' | 'compact'
  showPercentage?: boolean
}

export const SyncProgressBar: React.FC<SyncProgressBarProps> = ({
  progress,
  currentStage,
  system,
  variant = 'full',
  showPercentage = true
}) => {
  const getSystemColor = () => {
    switch(system) {
      case 'lightspeed': 
        return poolwerxColors.teal
      case 'xero': 
        return '#1E88E5'
      case 'skimmer':
        return '#FF9800'
      default: 
        return poolwerxColors.grey[500]
    }
  }

  // Ensure progress is a valid number between 0 and 100
  const safeProgress = Math.min(100, Math.max(0, Number(progress) || 0))

  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress 
          variant="determinate" 
          value={safeProgress}
          sx={{ 
            flex: 1, 
            height: 6,
            backgroundColor: poolwerxColors.grey[200],
            '& .MuiLinearProgress-bar': {
              backgroundColor: getSystemColor()
            }
          }}
        />
        {showPercentage && (
          <Typography variant="caption" sx={{ minWidth: 35 }}>
            {safeProgress}%
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="textSecondary">
          {currentStage}
        </Typography>
        {showPercentage && (
          <Typography variant="caption" fontWeight={600}>
            {safeProgress}%
          </Typography>
        )}
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={safeProgress}
        sx={{ 
          height: 8,
          borderRadius: 4,
          backgroundColor: poolwerxColors.grey[200],
          '& .MuiLinearProgress-bar': {
            backgroundColor: getSystemColor(),
            borderRadius: 4
          }
        }}
      />
    </Box>
  )
}

export default SyncProgressBar